import React from "react";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// Dual-series curved area chart drawn as inline vector markup, used by
// GoldenHourChart.
//
// Geometry (matches gifted-charts-core's LineChart/index.js + utils, the
// reference this was measured against):
//  - maxValue = round the combined (both series) data max UP to the next
//    multiple of 10 (already-multiple → +10). Shared across both series.
//  - Plot area height = `height`; gifted adds a 10px top pad
//    (extendedContainerHeight = height + 10). value→y:
//      y = (height + 10) - value * height / maxValue
//  - Points at x = yAxisLabelWidth + initialSpacing + i * spacing. We bake the
//    yAxisLabelWidth offset into the SVG coordinate space so the caller's
//    absolutely-positioned golden-hour overlay + hover zones (which use the
//    same formula) line up exactly.
//  - Curve: gifted's Catmull-Rom-style cubic. Each segment's control points are
//    current ± unit(opposed-neighbor-line) * (|opposed line| * curvature).
//  - Area fill closes down to baseline y = height + 9 (extended - xAxisThickness)
//    across the first→last point x-range.
//  - Vertical gradient: top = startFillColor, bottom = endFillColor.
//  - Dashed rules ("dashWidth,dashGap") at y = i * stepHeight, i=0..noOfSections.
//  - Y-axis labels right-aligned in the yAxisLabelWidth column; x-axis labels
//    centered under each point that carries one.
// The chart is static: the line and area draw at rest with no enter animation.

export type LineChartDatum = Omittable<{
  value: number;
  label?: string;
  labelTextStyle?: { color?: string; fontSize?: number; textAlign?: "center" };
}>;

export interface GoldenHourLineChartProps {
  data: LineChartDatum[];
  data2: LineChartDatum[];
  color1: string;
  color2: string;
  thickness1: number;
  thickness2: number;
  startFillColor1: string;
  endFillColor1: string;
  startFillColor2: string;
  endFillColor2: string;
  curvature: number;
  noOfSections: number;
  height: number;
  spacing: number;
  initialSpacing: number;
  endSpacing: number;
  yAxisLabelWidth: number;
  yAxisTextStyle: { color: string; fontSize: number };
  rulesColor: string;
  dashWidth: number;
  dashGap: number;
  xAxisLabelsVerticalShift: number;
  labelsExtraHeight: number;
}

const X_AXIS_THICKNESS = 1;
const TOP_PAD = 10; // gifted overflowTop(0) + 10

function roundUpToNext10(v: number): number {
  const m = v % 10;
  return v + (10 - m);
}

// gifted `line(A,B)` → {length, angle}
function lineMeta(a: [number, number], b: [number, number]) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return { length: Math.hypot(dx, dy), angle: Math.atan2(dy, dx) };
}

// gifted `controlPoint(curvature, current, previous, next, reverse)`
function controlPoint(
  curvature: number,
  current: [number, number],
  previous: [number, number] | undefined,
  next: [number, number] | undefined,
  reverse?: boolean,
): [number, number] {
  const p = previous ?? current;
  const n = next ?? current;
  const o = lineMeta(p, n);
  const angle = o.angle + (reverse ? Math.PI : 0);
  const length = o.length * curvature;
  return [current[0] + Math.cos(angle) * length, current[1] + Math.sin(angle) * length];
}

// gifted `svgPath` (CUBIC) → the curve `d` string
function curvePath(points: [number, number][], curvature: number): string {
  return points.reduce((acc, point, i, a) => {
    if (i === 0) return `M${point[0]},${point[1]}`;
    const [cpsX, cpsY] = controlPoint(curvature, at(a, i - 1), a[i - 2], point);
    const [cpeX, cpeY] = controlPoint(curvature, point, a[i - 1], a[i + 1], true);
    return `${acc} C${cpsX},${cpsY} ${cpeX},${cpeY} ${point[0]},${point[1]}`;
  }, "");
}

export function GoldenHourLineChart({
  data,
  data2,
  color1,
  color2,
  thickness1,
  thickness2,
  startFillColor1,
  endFillColor1,
  startFillColor2,
  endFillColor2,
  curvature,
  noOfSections,
  height,
  spacing,
  initialSpacing,
  endSpacing,
  yAxisLabelWidth,
  yAxisTextStyle,
  rulesColor,
  dashWidth,
  dashGap,
  xAxisLabelsVerticalShift,
  labelsExtraHeight,
}: GoldenHourLineChartProps) {
  const n = Math.max(data.length, data2.length);

  const rawMax = Math.max(
    0,
    ...data.map((d) => d.value),
    ...data2.map((d) => d.value),
  );
  const maxValue = roundUpToNext10(rawMax) || 10;

  const extendedHeight = height + TOP_PAD;
  const baselineY = extendedHeight - X_AXIS_THICKNESS; // area fill baseline
  const stepHeight = height / noOfSections;
  const stepValue = maxValue / noOfSections;

  // x baked with the yAxisLabelWidth offset so caller overlays align.
  const getX = (i: number) => yAxisLabelWidth + initialSpacing + i * spacing;
  const getY = (value: number) => extendedHeight - (value * height) / maxValue;

  const pts1: [number, number][] = data.map((d, i) => [getX(i), getY(d.value)]);
  const pts2: [number, number][] = data2.map((d, i) => [getX(i), getY(d.value)]);

  const line1 = curvePath(pts1, curvature);
  const line2 = curvePath(pts2, curvature);

  const firstX = getX(0);
  const lastX = getX(n - 1);
  const area1 = `M ${firstX},${baselineY} L ${firstX},${getY(data[0]?.value ?? 0)} ${line1} L ${lastX},${baselineY} L ${firstX},${baselineY}`;
  const area2 = `M ${firstX},${baselineY} L ${firstX},${getY(data2[0]?.value ?? 0)} ${line2} L ${lastX},${baselineY} L ${firstX},${baselineY}`;

  // Rules extent: from the plot left edge to the last point + endSpacing.
  const ruleX1 = yAxisLabelWidth;
  const ruleX2 = lastX + endSpacing;

  const svgWidth = ruleX2;
  // Room below the plot for x-axis labels (gifted reserves ~ this band).
  const labelBand = 6 + 18 + xAxisLabelsVerticalShift;
  const svgHeight = extendedHeight + labelBand;

  const dashArray = `${dashWidth},${dashGap}`;

  return (
    <svg
      width={svgWidth}
      height={svgHeight}
      viewBox={`0 0 ${svgWidth} ${svgHeight}`}
    >
      <defs>
        <linearGradient id="gh-grad-1" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={startFillColor1} stopOpacity={1} />
          <stop offset="1" stopColor={endFillColor1} stopOpacity={1} />
        </linearGradient>
        <linearGradient id="gh-grad-2" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={startFillColor2} stopOpacity={1} />
          <stop offset="1" stopColor={endFillColor2} stopOpacity={1} />
        </linearGradient>
      </defs>

      {/* Dashed grid rules (i=0 top … i=noOfSections baseline) */}
      {Array.from({ length: noOfSections + 1 }, (_, i) => {
        const y = TOP_PAD + stepHeight * i;
        return (
          <line
            key={`rule-${i}`}
            x1={ruleX1}
            y1={y}
            x2={ruleX2}
            y2={y}
            stroke={rulesColor}
            strokeWidth={1}
            strokeDasharray={dashArray}
          />
        );
      })}

      {/* Y-axis labels: value = maxValue - stepValue*i */}
      {Array.from({ length: noOfSections + 1 }, (_, i) => {
        const value = maxValue - stepValue * i;
        const y = TOP_PAD + stepHeight * i;
        return (
          <text
            key={`ylabel-${i}`}
            x={yAxisLabelWidth - 6}
            y={y + 3}
            fontSize={yAxisTextStyle.fontSize}
            fill={yAxisTextStyle.color}
            textAnchor="end"
          >
            {String(Math.round(value))}
          </text>
        );
      })}

      {/* Series 1 (drawn first, under series 2) */}
      <path d={area1} fill="url(#gh-grad-1)" />
      <path d={line1} fill="none" stroke={color1} strokeWidth={thickness1} />

      {/* Series 2 */}
      <path d={area2} fill="url(#gh-grad-2)" />
      <path d={line2} fill="none" stroke={color2} strokeWidth={thickness2} />

      {/* X-axis labels */}
      {data.map((d, i) =>
        d.label ? (
          <text
            key={`xlabel-${i}`}
            x={getX(i)}
            y={extendedHeight + 6 + (d.labelTextStyle?.fontSize ?? 9)}
            fontSize={d.labelTextStyle?.fontSize ?? 9}
            fill={d.labelTextStyle?.color ?? yAxisTextStyle.color}
            textAnchor="middle"
          >
            {d.label}
          </text>
        ) : null,
      )}
    </svg>
  );
}
